import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from 'redux/user/selectors';
import {
  addNotification,
  reloadNotifications,
  removeNotification,
  setNotifications,
  updateNotification,
} from 'redux/notifications';
import { AsyncJobStatus, AsyncJobType } from 'types';
import { setDataManagementRefresh } from 'redux/dataManagement';
import { showNotification } from '../common/function';
import { getConfigTestCopyText } from 'components/UserSettings/HRIS/WorkdayRaaS';

const RECONNECT_TIMEOUT = 5 * 1000;

export const createWebSocketConnection = (dispatch) => {
  // TODO: Our docker compose instance should run nginx with an SSL certificate,
  // so we can front the application the same way infrastructure does (ie: same
  // port, protocol, CORS settings, CSP, etc...  Until then, we have removed
  // NOTIFICATION_SOCKET_URL and need to interpolate on the client.  This is a
  // workaround for local development until things are behind an nginx proxy.
  const hostname = window.location.hostname === 'localhost' ? 'localhost:8999' : window.location.hostname;

  const proto = window.location.protocol.replace('http', 'ws');
  const notificationSocketUrl = `${proto}//${hostname}/ws`;
  const socket = new WebSocket(notificationSocketUrl);

  socket.onmessage = function (event) {
    const { type, payload } = JSON.parse(event.data);

    switch (type) {
      case 'INITIAL_STATE':
        dispatch(setNotifications(payload));
        break;
      case 'JOB_CREATED':
        dispatch(addNotification(payload));
        break;
      case 'JOB_UPDATED':
        if ([AsyncJobStatus.DONE, AsyncJobStatus.FAILED].includes(payload.status)) {
          handleJobCompleted(payload)
          dispatch(removeNotification(payload));
        } else {
          dispatch(updateNotification(payload));
        }
        break;
      case 'DATA_MANAGEMENT':
        dispatch(setDataManagementRefresh(payload));
        break;
      default:
        break;
    }
  };

  function handleJobCompleted(job) {
    switch (job.type) {
      case AsyncJobType.TEST_HRIS_URL:
        if (job.result?.hrisErrorMessage || job.result?.error) {
          const errorMessage = job.result.hrisErrorMessage || 'Unknown error occurred';
          showNotification(errorMessage, 'HRIS Test Failed', 'error');
        } else {
          const configName = job.details?.configName || 'HRIS'

          showNotification(
            `${getConfigTestCopyText(configName)} test completed successfully`,
            'HRIS Test Success',
            'success'
          );
        }
        break;
    }
  }

  socket.onclose = function (event) {
    console.log('Socket closed', event);

    setTimeout(() => {
      console.log('Trying reconnect to socket server');
      createWebSocketConnection(dispatch);
    }, RECONNECT_TIMEOUT);
  };

  return socket;
};

const NOTIFICATIONS_FALLBACK_PERIOD = 60000;

export const useNotificationService = () => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();

  useEffect(() => {
    const socket = user?.id ? createWebSocketConnection(dispatch) : null;
    const fallbackReload = user?.id
      ? setInterval(
        () => { dispatch(reloadNotifications()) },
        NOTIFICATIONS_FALLBACK_PERIOD
      )
      : null;

    return () => {
      if (socket) {
        socket.close();
      }

      if (fallbackReload) {
        clearInterval(fallbackReload);
      }
    };
  }, [user?.id, dispatch]);
};
